/* eslint-disable complexity */
import { Icon16InfoOutline } from '@vkontakte/icons';
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import throttle from 'lodash.throttle';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_BLOCKED, IS_FROZEN, IS_MY_TEAM, IS_PUBLIC, IS_PUBLIC_OF_OVERQUOTA } from 'reactApp/appHelpers/configHelpers';
import { trashbinMoveToSafeButton } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { publicOverquota } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import { TOP_TOOLBAR_ID } from 'reactApp/constants/magicIdentificators';
import { getFilesWithSelectedFace } from 'reactApp/modules/faces/faces.selectors';
import { FeatureSelector } from 'reactApp/modules/features/components/FeatureSelector';
import { getFeatureRequiredSignUpWhenDownloading } from 'reactApp/modules/features/features.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { isPublicUploadEnabled } from 'reactApp/modules/public/public.selectors';
import { requiredAuthorizationHelpers } from 'reactApp/modules/requiredAuthorization/helpers';
import { reDownloadController } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentRouteId, getCurrentStorage, selectStatusPage } from 'reactApp/modules/router/router.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { sidebarToggle } from 'reactApp/modules/settings/settings.module';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getIdsCount, getSelectedItems, isFolderWithAuthor, isRootCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { SetupVigboGalleryTooltip } from 'reactApp/sections/VigboPage/SetupVigboGalleryTooltip/SetupVigboGalleryTooltip';
import type { RootState } from 'reactApp/store';
import { useAiOrganizeFakedoorActions } from 'reactApp/ui/Toolbar/customActions/useAiOrganizeFakedoorActions';
import { useCustomActions } from 'reactApp/ui/Toolbar/customActions/useCustomActions.new';
import { shouldCollapse } from 'reactApp/ui/Toolbar/Toolbar.helpers';
import { type ToolbarProps, type TToolbarItem, ToolbarGapMode } from 'reactApp/ui/Toolbar/Toolbar.types';
import { DelimiterItem } from 'reactApp/ui/Toolbar/ToolbarItem/DelimiterItem';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';
import { useActions } from 'reactApp/ui/Toolbar/useActions.new';
import { useFilterActions } from 'reactApp/ui/Toolbar/useFilterActions';
import { Icon20MenuOutline } from 'reactApp/ui/VKUIIcons';
import { useWebSearchProps } from 'reactApp/ui/WebSearch/helpers';
import { WebSearch } from 'reactApp/ui/WebSearch/WebSearch';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import useResizeObserver from 'use-resize-observer/polyfilled';

import { storages } from '../../../server/constants/storages';
import { FloatingTooltip } from '../FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement } from '../FloatingTooltip/FloatingTooltip.types';
import { useVigboRightActions } from './customActions/useVigboRightActions';
import styles from './Toolbar.new.css';

const MAX_NUM_OF_BUTTON_POSITION_TRIES = 10;
const SETUP_VIGBO_GALLERY_PROMO_ID = 'setup-vigbo-gallery-tooltip';

// eslint-disable-next-line max-lines-per-function
export const ToolbarNew = memo(
    // eslint-disable-next-line max-lines-per-function
    ({ gapMode, toolbarType, isEbookReader, color, onBookmark, onDownload, isResponsiveEnable }: ToolbarProps): ReactElement => {
        const rootRef = useRef<HTMLDivElement | null>(null);
        const leftRef = useRef<HTMLDivElement | null>(null);
        const rightRef = useRef<HTMLDivElement | null>(null);
        const counterRef = useRef(0);
        const timerRef = useRef<number | null>(null);
        const isPublicOfOverquota = IS_PUBLIC_OF_OVERQUOTA && publicOverquota;

        const downloadButtonRef = useRef<HTMLDivElement>(null);
        const setupVigboGalleryButtonRef = useRef<HTMLDivElement>(null);

        const dispatch = useDispatch();
        const storage = useSelector(getCurrentStorage) as EStorageType;
        const { isPublic, isIntegration, isStart, isHome, isSearch, isTrashBin, isVigbo } = getStorage(storage);
        const isSuccessAuthWhenDownload = useSelector(requiredAuthorizationHelpers.isSuccessAuthWhenDownload);
        const itemsCount = useSelector((state: RootState) => getIdsCount(state, storage as EStorageType));
        const isRootFolder = useSelector((state: RootState) => isRootCurrentFolder(state, storage));
        const hasHomeItems = itemsCount !== 0 && isHome && isRootFolder;

        const isSelectingMode = useSelector(SelectionsSelectors.isAnyIndexSelected);
        const isErrorPage = useSelector(selectStatusPage);
        const searchAvailable = Boolean(storages[storage]?.isSearchAvailable) && !IS_FROZEN && !IS_BLOCKED && !isErrorPage;
        const webSearchProps = useWebSearchProps();
        const showWebSearch = IS_MY_TEAM && ((!isSelectingMode && searchAvailable && hasHomeItems) || isSearch || isStart);
        const showActions = !webSearchProps.isExpanded && hasHomeItems;

        let filters = useFilterActions({ storage, toolbarType });
        const vigboRightActions = useVigboRightActions({ storage, toolbarType, setupVigboGalleryButtonRef });
        const aiOrganizeFakedoorActions = useAiOrganizeFakedoorActions({ storage });

        const currentRouteId = useSelector(getCurrentRouteId);
        if (currentRouteId === '/') {
            filters = filters.filter((item) => item.id !== 'author');
        }

        const actions = useActions({
            storage,
            toolbarType,
            isEbookReader,
            downloadButtonRef,
            onDownload,
            isPublicOfOverquota,
        });
        const { left: leftCustomActions, right: rightCustomActions } = useCustomActions({
            storage,
            isEbookReader,
            onBookmark,
        });
        const filesWithFaces = useSelector(getFilesWithSelectedFace);
        const isPublicUpload = useSelector(isPublicUploadEnabled);
        const folderWithAuthors = useSelector(isFolderWithAuthor);
        const isAnonymous = useSelector(UserSelectors.isAnonymous);
        const facesIsOff = !filesWithFaces.length;
        const selectedItems = useSelector(getSelectedItems);
        const setupVigboGalleryTooltip = !storeHelper.getValue(SETUP_VIGBO_GALLERY_PROMO_ID);

        const onSidebarTogglerClick = useCallback(() => {
            dispatch(sidebarToggle());
        }, [dispatch]);

        const showAuthorsFilter = (folderWithAuthors || (isPublic && isPublicUpload)) && facesIsOff && !isAnonymous;

        const [priority, setPriority] = useState(0);

        let leftActions = [...leftCustomActions, ...actions];
        let rightActions = [...aiOrganizeFakedoorActions, ...vigboRightActions, ...filters, ...rightCustomActions];

        if (itemsCount === 0 && !showAuthorsFilter) {
            leftActions = [...actions];

            if (trashbinMoveToSafeButton && isTrashBin) {
                rightActions = [...rightCustomActions];
            } else if (isVigbo) {
                rightActions = [...vigboRightActions];
            } else {
                rightActions = [...aiOrganizeFakedoorActions];
            }
        }

        if (isEbookReader) {
            rightActions = [...leftCustomActions, ...actions, ...rightCustomActions];
            leftActions = [];
        }

        if (isPublicOfOverquota) {
            leftActions = [...actions];
            rightActions = [];
        }

        const setResetStackCounter = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = window.setTimeout(() => {
                counterRef.current = 0;
            }, 1000);
        };

        const findButtonPriority = useCallback((): void => {
            if (leftActions.length && leftRef?.current && rootRef?.current) {
                const leftWidth = leftRef.current.getBoundingClientRect()?.width;
                const rightWidth = rightRef?.current ? rightRef?.current.getBoundingClientRect()?.width : 0;
                const rootWidth = rootRef.current.getBoundingClientRect()?.width;

                const emptyPlace = rootWidth - leftWidth - rightWidth;

                if (emptyPlace <= 10 && priority < 20) {
                    if (counterRef.current > MAX_NUM_OF_BUTTON_POSITION_TRIES) {
                        // useState для этого каунтера использовать нельзя, так как его изменение вызывает перерендер и сбрасывая каунтер, мы опять попадаем в начало этого цикла
                        setResetStackCounter();
                        return;
                    }
                    setPriority(priority + 1);
                    counterRef.current++;
                } else if (
                    emptyPlace > 150 &&
                    priority > 0 &&
                    storage !== EStorageType.attaches &&
                    storage !== EStorageType.favorites &&
                    storage !== EStorageType.sharedIncoming &&
                    storage !== EStorageType.albums &&
                    storage !== EStorageType.vigbo
                ) {
                    // CLOUDWEB-17853: На разделах "Из почты", "Избранные", "Совместные" работало некорректно
                    setPriority(priority - 1);
                    counterRef.current++;
                }

                // Если будет зацикливание в рендере тулбара, то в вызове только в этом месте поднять таймаут с 1000
                setResetStackCounter();
            }
        }, [leftActions.length, priority]);

        const renderItem = useCallback(
            ({ Item, itemProps, ...data }: TToolbarItem, index) => {
                if (Item) {
                    return <Item key={itemProps?.id || index} color={color} parentRef={rootRef} {...itemProps} />;
                }

                if (data.id === EActions.delimiter) {
                    return <DelimiterItem color={color} />;
                }

                return (
                    <ToolbarItem
                        key={data.id}
                        ref={data.ref}
                        id={data.id}
                        text={data.text}
                        onClick={data.onClick}
                        onShow={data.onShow}
                        hint={data.hint}
                        icon={data.icon}
                        color={data?.color || color}
                        collapse={
                            isEbookReader && data.id !== EActions.publish
                                ? true
                                : shouldCollapse(data, priority, Boolean(isResponsiveEnable))
                        }
                        list={data.list}
                        parentRef={rootRef}
                        dropdownTheme={data.dropdownTheme}
                        dropdownPosition={data.dropdownPosition}
                        withoutDropdownIcon={data.withoutDropdownIcon}
                        openDropdownByArrow={data.openDropdownByArrow}
                        tooltipId={data?.tooltipId}
                        disabled={data.disabled}
                    />
                );
            },
            [priority, isResponsiveEnable, color]
        );

        const handleOnResize = useMemo(() => throttle(findButtonPriority, 500), [findButtonPriority]);

        useEffect(handleOnResize, [priority, leftActions.length, rightActions.length]);

        useResizeObserver({ onResize: handleOnResize, ref: rootRef });

        return (
            <div className={styles.root} ref={rootRef} id={TOP_TOOLBAR_ID} data-qa-id="toolbar">
                {/* tempexp_15344-next-line */}
                <FeatureSelector
                    selector={getFeatureRequiredSignUpWhenDownloading}
                    control={<></>}
                    variant1={
                        IS_PUBLIC &&
                        !reDownloadController.isDownloadHappened() && (
                            <FloatingTooltip
                                target={downloadButtonRef.current}
                                title=""
                                text="Теперь файл можно скачать"
                                placement={ETooltipPlacement.bottom}
                                closeOnOutsideClick={false}
                                qaId="required-auth-when-download"
                                onShow={() => requiredAuthorizationHelpers.sendGa('shw-redwnld-tltip', 'variant1', 'folder')}
                                onClick={() => requiredAuthorizationHelpers.sendGa('clck-redwnld-tltip', 'variant1', 'folder')}
                                onClose={() => requiredAuthorizationHelpers.sendGa('cls-redwnld-tltip', 'variant1', 'folder')}
                            />
                        )
                    }
                    variant2={<></>}
                    options={{ skipCondition: { variant1: () => !isSuccessAuthWhenDownload } }}
                />
                {setupVigboGalleryTooltip && setupVigboGalleryButtonRef?.current && (
                    <SetupVigboGalleryTooltip
                        target={setupVigboGalleryButtonRef.current}
                        title="Перейти к работе в Vigbo"
                        text="Выбрать оформление галереи и посмотреть, что получается"
                        placement={ETooltipPlacement.bottomEnd}
                        closeOnContextMenu
                        usePortal
                        onClose={() => {
                            storeHelper.markAsShown(SETUP_VIGBO_GALLERY_PROMO_ID);
                            sendPaymentGa({
                                eventCategory: ECategoryGa.tooltip,
                                action: 'click',
                                name: 'goto-vigbo',
                            });
                        }}
                        onShow={() => {
                            sendPaymentGa({
                                eventCategory: ECategoryGa.tooltip,
                                action: 'show',
                                name: 'goto-vigbo',
                            });
                        }}
                    />
                )}
                {isIntegration && !!selectedItems?.length && (
                    <div className={styles.disclaimer}>
                        <div>
                            <Icon16InfoOutline width={20} height={21} fill="var(--vkui--color_icon_accent)" />
                        </div>
                        <Text>Просматривать смогут все по ссылке</Text>
                    </div>
                )}

                {isResponsiveEnable && <ToolbarItem id="sidebarToggler" icon={<Icon20MenuOutline />} onClick={onSidebarTogglerClick} />}

                {(!IS_MY_TEAM || showActions || isSelectingMode) && (
                    <>
                        {Boolean(leftActions?.length) && (
                            <div
                                className={classNames(styles.left, {
                                    [styles.wideGap]: gapMode === ToolbarGapMode.wide,
                                })}
                                ref={leftRef}
                            >
                                {leftActions.map(renderItem)}
                            </div>
                        )}

                        {Boolean(rightActions?.length) && (
                            <div
                                className={classNames(styles.right, {
                                    [styles.wideGap]: gapMode === ToolbarGapMode.wide,
                                    [styles.withWebSearch]: showWebSearch,
                                    [styles.vigboSection]: isVigbo,
                                })}
                                ref={rightRef}
                            >
                                {rightActions.map(renderItem)}
                            </div>
                        )}
                    </>
                )}

                {showWebSearch && (
                    <div className={styles.webSearch}>
                        <WebSearch {...webSearchProps} />
                    </div>
                )}
            </div>
        );
    }
);

ToolbarNew.displayName = 'Toolbar';
